import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, orderBy, limit } from "../services/firebase";

const firestoreErrorCode = (error) => (error?.code || "").replace(/^firestore\//, "");

const toSafeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : 0;
};

export function normalizeRankingEntry(rawEntry, id = "") {
  const entry = rawEntry && typeof rawEntry === "object" ? rawEntry : {};
  const nameCandidate = entry.name ?? entry.author ?? entry.profile?.name;
  const name = typeof nameCandidate === "string" && nameCandidate.trim()
    ? nameCandidate.trim()
    : "여행자";

  return {
    ...entry,
    id: id || (typeof entry.id === "string" ? entry.id : ""),
    name,
    reviewCount: toSafeCount(entry.reviewCount ?? (Array.isArray(entry.reviews) ? entry.reviews.length : 0)),
    badges: Array.isArray(entry.badges) ? entry.badges.filter(badge => typeof badge === "string") : [],
    totalLikes: toSafeCount(entry.totalLikes ?? entry.likes),
    planCount: toSafeCount(entry.planCount ?? (entry.sharedAt ? 1 : 0)),
    mbti: typeof entry.mbti === "string" ? entry.mbti : "",
  };
}

export function useRanking(fbDb, firebaseUid) {
  const [rankingData, setRankingData] = useState([]);
  const [rankingLoaded, setRankingLoaded] = useState(false);
  const [rankingTab, setRankingTab] = useState("rank");
  const [rankingError, setRankingError] = useState("");

  const loadRanking = useCallback(async () => {
    if (!fbDb) {
      setRankingError("랭킹을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.");
      setRankingLoaded(true);
      return false;
    }

    setRankingLoaded(false);
    setRankingError("");
    try {
      const qy = query(collection(fbDb, "community"), orderBy("likes", "desc"), limit(20));
      const snap = await getDocs(qy);
      setRankingData(snap.docs.map(rankingDoc => normalizeRankingEntry(rankingDoc.data(), rankingDoc.id)));
      return true;
    } catch (e) {
      const code = firestoreErrorCode(e);
      if (code === "permission-denied") {
        console.warn("[ranking-load] Firestore 권한 거부로 랭킹 목록을 비웁니다. 배포된 규칙을 확인해주세요.", e);
        setRankingData([]);
        setRankingError("");
        return true;
      }
      console.warn("랭킹 로드 실패:", e);
      setRankingError(
        code === "unavailable"
          ? "랭킹 서비스에 일시적으로 연결할 수 없습니다. 잠시 후 다시 시도해주세요."
          : "랭킹을 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
      );
      return false;
    } finally {
      // 실패해도 로딩 상태는 해제 — 무한 "불러오는 중" 방지, 빈 상태 UI로 전환
      setRankingLoaded(true);
    }
  }, [fbDb]);

  useEffect(() => { loadRanking(); }, [firebaseUid, loadRanking]);

  return {
    rankingData, setRankingData,
    rankingLoaded, setRankingLoaded,
    rankingError, setRankingError,
    rankingTab, setRankingTab,
    loadRanking,
  };
}
